import { CommonModule } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router, RouterLink } from "@angular/router";
import { Subscription } from "rxjs";

import { abrirDialogoNuevaVersion } from "../../modal-agregar-version/modal-agregar-version.component";
import { CODIGOS_VERSIONES, EtapaVersiones, VersionResumen } from "../etapas-versiones.model";
import { EtapasVersionesService } from "../etapas-versiones.service";
import {
  ChipEstado,
  LEYENDA_ENTREGA_AUTOMATICA,
  SIN_DATO,
  chipEstadoVersion,
  chipsEstadoEtapa,
  claveEtapa,
  etapaDeClave,
  fechaCorta,
  fechaDeVersion,
  fechasDeEtapa,
  mensajeVersionCreada,
  porcentajeConVobo,
  resumenVersiones,
  textoAbiertas,
  textoAbiertasEtapa,
  textoCerradaDesde,
  textoEsperanVersion,
  textoIncidencias,
  tituloFueraDeFecha,
  versionesRecientesPrimero,
} from "../etapas-versiones.util";
import { rutaDetalleVersion } from "../proyecto-pagina.rutas";
import { TextoUltimaPrueba, nombreCiclo, textoUltimaPrueba } from "../pruebas-version.util";
import { abrirRegistrarEjecucion } from "../registrar-ejecucion/registrar-ejecucion.component";

/** Una etapa de la lista de la izquierda, ya con sus textos. */
interface FilaEtapa {
  clave: string;
  etapa: EtapaVersiones;
  /** El estado y, si aplica, «Fuera de fecha». */
  chips: ChipEstado[];
  /** El title de «Fuera de fecha»: cuándo terminó y cuántas abiertas tiene. */
  fueraDeFecha: string | null;
  /** «Día 13 de 31», «Inicia el …», «Terminó el …»; null fuera de fecha (lo dice el chip). */
  detalle: string | null;
  fechas: string;
  versiones: string;
  abiertas: string | null;
}

/** Una versión de la tabla, ya con sus textos. */
interface FilaVersion {
  version: VersionResumen;
  creada: string;
  chip: ChipEstado;
  desde: string | null;
  /** «Regresión 2», el chip del resultado y «115/120»; null sin ejecuciones. */
  ultima: TextoUltimaPrueba | null;
  /** «115/120 aprobados · 14/08/2026»; null sin casos ni fecha. */
  lineaPrueba: string | null;
  /** El detalle de la versión (A2); null si se guardó sin id. */
  ruta: string | null;
}

/**
 * Página del proyecto · pestaña «Etapas y versiones» (A1; plan
 * docs/superpowers/plans/2026-10-13-etapas-y-versiones.md §8 y maqueta
 * Main.dc.html). A la izquierda las etapas del proyecto con su estado; a la
 * derecha la etapa de ?etapa= (o la primera): sus indicadores y la tabla de
 * versiones. Lo lee de PROYECTOS_ETAPAS_VERSIONES y no recalcula nada.
 *
 * - «Nueva versión» abre T1 (solo con puedeCrearVersion, D4 y D8) y, al
 *   crearla, vuelve a leer la etapa.
 * - «Crear etapa» y «Editar etapa» los resuelve la página (llevan la copia del
 *   proyecto y la URL de regreso a los editores).
 * - Fase 4: «Pruebas ejecutadas» y «Última prueba» (tipo y ciclo, resultado,
 *   aprobados/ejecutados y fecha) salen del servidor; cada versión lleva
 *   «Ver detalle» (A2) y, con puedeRegistrarPruebas (D4 y D8), «Registrar
 *   pruebas» (T2 con esa versión elegida).
 * - T3b (Fase 3, maqueta A1p): «N resoluciones esperan versión · Nueva
 *   versión» sobre la tabla si la etapa tiene pendientes (se entregan con o
 *   sin bandera) y, solo con la bandera (entregaAutomatica), la leyenda «Las
 *   resoluciones se entregan en la siguiente versión QA de la etapa».
 */
@Component({
  selector: "app-etapas-versiones",
  standalone: true,
  templateUrl: "./etapas-versiones.component.html",
  styleUrls: ["./etapas-versiones.component.css"],
  imports: [CommonModule, MatButtonModule, RouterLink],
})
export class EtapasVersionesComponent implements OnChanges, OnDestroy {
  /** El _id del proyecto en Mongo. */
  @Input() idProyecto: string | null = null;
  @Input() nombreProyecto: string | null = null;
  /** Para que T1 diga por qué no se puede crear (D8). */
  @Input() estatusProyecto: string | null = null;
  /** ?etapa=: el _id de la etapa, o «pos-N» si se guardó sin él. */
  @Input() claveSeleccionada: string | null = null;

  /** El usuario eligió otra etapa en la lista (la página la pone en ?etapa=). */
  @Output() readonly elegirEtapa = new EventEmitter<string>();
  @Output() readonly crearEtapa = new EventEmitter<void>();
  @Output() readonly editarEtapa = new EventEmitter<EtapaVersiones>();

  readonly sinDato = SIN_DATO;
  readonly leyendaEntrega = LEYENDA_ENTREGA_AUTOMATICA;

  etapas: FilaEtapa[] = [];
  seleccionada: FilaEtapa | null = null;
  versiones: FilaVersion[] = [];
  cargando = false;
  error = "";

  private lectura?: Subscription;

  constructor(
    private readonly servicio: EtapasVersionesService,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly cdr: ChangeDetectorRef,
    private readonly router: Router,
  ) {}

  ngOnChanges(cambios: SimpleChanges): void {
    if (cambios["idProyecto"]) {
      this.cargar();
    } else if (cambios["claveSeleccionada"]) {
      this.seleccionar();
    }
  }

  ngOnDestroy(): void {
    this.lectura?.unsubscribe();
  }

  /* ============================ Lo que pinta la plantilla ============================ */

  get etapa(): EtapaVersiones | null {
    return this.seleccionada?.etapa ?? null;
  }

  get textoIncidencias(): string {
    return textoIncidencias(this.etapa?.indicadores);
  }

  get textoAbiertas(): string {
    return textoAbiertas(this.etapa?.indicadores);
  }

  get porcentajeConVobo(): number | null {
    return porcentajeConVobo(this.etapa?.indicadores);
  }

  get sinVersionIdentificada(): number {
    return this.etapa?.indicadores?.sinVersionIdentificada ?? 0;
  }

  /** Bajo «Pruebas ejecutadas»: «Última: 14/08/2026» o «Sin pruebas registradas». */
  get textoUltimaEjecucion(): string {
    const fecha = fechaCorta(this.etapa?.indicadores?.ultimaPrueba?.fecha);
    return fecha ? `Última: ${fecha}` : "Sin pruebas registradas";
  }

  /** «3 resoluciones esperan versión» (T3b); null si la etapa no tiene pendientes. */
  get esperanVersion(): string | null {
    return textoEsperanVersion(this.etapa?.indicadores?.pendientesVersion);
  }

  trackEtapa(_: number, fila: FilaEtapa): string {
    return fila.clave;
  }

  trackVersion(indice: number, fila: FilaVersion): string {
    return fila.version.id ?? `${indice}`;
  }

  /* ============================ Acciones ============================ */

  /** Lee las etapas del proyecto; un rechazo del servidor (pCodRetorno) se dice tal cual. */
  cargar(): void {
    this.lectura?.unsubscribe();
    this.error = "";
    const id = (this.idProyecto ?? "").trim();
    if (!id) {
      this.etapas = [];
      this.seleccionar();
      return;
    }
    this.cargando = true;
    this.lectura = this.servicio.etapasVersiones(id).subscribe({
      next: (respuesta) => {
        this.cargando = false;
        if (respuesta?.pCodRetorno === CODIGOS_VERSIONES.EXITO) {
          this.etapas = (respuesta.etapas ?? []).map((etapa, indice) => this.filaEtapa(etapa, indice));
        } else {
          this.etapas = [];
          this.error = respuesta?.pMensaje?.trim() || "No se pudieron consultar las etapas del proyecto.";
        }
        this.seleccionar();
      },
      error: () => {
        this.cargando = false;
        this.etapas = [];
        this.error = "No se pudieron consultar las etapas del proyecto. Revisa tu conexión e intenta de nuevo.";
        this.seleccionar();
      },
    });
  }

  elegir(fila: FilaEtapa): void {
    if (fila.clave !== this.seleccionada?.clave) {
      this.elegirEtapa.emit(fila.clave);
    }
  }

  /** «Nueva versión» (T1) en la etapa elegida; al crearla, vuelve a leer las etapas. */
  nuevaVersion(): void {
    const etapa = this.etapa;
    if (!etapa?.puedeCrearVersion || !this.idProyecto || this.dialog.openDialogs.length > 0) {
      return;
    }
    abrirDialogoNuevaVersion(this.dialog, {
      idProyecto: this.idProyecto,
      idEtapa: etapa.idEtapa,
      nombreProyecto: this.nombreProyecto,
      nombreEtapa: etapa.nombre,
      estatusProyecto: this.estatusProyecto,
    })
      .afterClosed()
      .subscribe((resultado) => {
        if (!resultado) {
          return;
        }
        const aviso = mensajeVersionCreada(resultado);
        if (aviso) {
          this.snackBar.open(aviso, "Cerrar", { duration: 6000, horizontalPosition: "right", verticalPosition: "top" });
        }
        this.cargar();
      });
  }

  /** «Registrar pruebas» (T2) en una versión de la etapa; al guardar, vuelve a leer las etapas. */
  registrarPruebas(version: VersionResumen): void {
    const etapa = this.etapa;
    if (!etapa?.puedeRegistrarPruebas || !etapa.idEtapa || !this.idProyecto || this.dialog.openDialogs.length > 0) {
      return;
    }
    abrirRegistrarEjecucion(this.dialog, {
      idProyecto: this.idProyecto,
      idEtapa: etapa.idEtapa,
      nombreProyecto: this.nombreProyecto,
      nombreEtapa: etapa.nombre,
      versiones: etapa.versiones ?? [],
      todasLasVersiones: true,
      idVersion: version.id,
      ejecutores: etapa.ejecutores ?? [],
      siguienteCiclo: etapa.siguienteCiclo,
      puedeRegistrarPruebas: etapa.puedeRegistrarPruebas,
    })
      .afterClosed()
      .subscribe((resultado) => {
        if (!resultado) {
          return;
        }
        const ejecucion = resultado.ejecucion;
        if (ejecucion) {
          this.snackBar.open(`Se registró la ejecución «${nombreCiclo(ejecucion.tipo, ejecucion.ciclo)}».`, "Cerrar", {
            duration: 6000,
            horizontalPosition: "right",
            verticalPosition: "top",
          });
        }
        this.cargar();
      });
  }

  /* ============================ Internas ============================ */

  /** La etapa de la clave (o la primera) y su tabla de versiones, de la más nueva a la más vieja. */
  private seleccionar(): void {
    const elegida = etapaDeClave(this.etapas.map((fila) => fila.etapa), this.claveSeleccionada);
    this.seleccionada = this.etapas.find((fila) => fila.etapa === elegida) ?? null;
    this.versiones = versionesRecientesPrimero(elegida?.versiones).map((version) => {
      const ultima = textoUltimaPrueba(version.ultimaPrueba);
      const casos = ultima?.casos ? `${ultima.casos} aprobados` : null;
      return {
        version,
        creada: fechaDeVersion(version),
        chip: chipEstadoVersion(version.estado),
        desde: textoCerradaDesde(version),
        ultima,
        lineaPrueba: [casos, ultima?.fecha].filter(Boolean).join(" · ") || null,
        ruta: version.id && this.idProyecto ? rutaDetalleVersion(this.router.url, this.idProyecto, version.id) : null,
      };
    });
    this.cdr.markForCheck();
  }

  private filaEtapa(etapa: EtapaVersiones, indice: number): FilaEtapa {
    const abiertas = etapa.indicadores?.abiertas ?? 0;
    const chips = chipsEstadoEtapa(etapa.estado);
    const fuera = chips.length > 1;
    return {
      clave: claveEtapa(etapa, indice),
      etapa,
      chips,
      fueraDeFecha: fuera ? tituloFueraDeFecha(etapa.fechaFin, abiertas) : null,
      detalle: fuera ? null : chips[0].detalle,
      fechas: fechasDeEtapa(etapa),
      versiones: resumenVersiones(etapa.versiones?.length, etapa.vigente?.nombre),
      abiertas: abiertas > 0 ? textoAbiertasEtapa(abiertas) : null,
    };
  }
}
